/**
 * Pure DSP primitives. No WebAudio, no Node APIs — just numbers in, samples out.
 *
 * Keeping synthesis pure buys three things at once:
 *   1. The browser runtime and the offline WAV baker share ONE implementation,
 *      so a trailer's audio cannot drift from the game's.
 *   2. It is unit-testable in Node (see test/synth.test.ts).
 *   3. It is deterministic, so a replay reproduces the exact same sound.
 */

/** Deterministic noise source. Mirrors engine/rng.ts so audio matches on replay. */
export class NoiseRng {
  private s: number;
  constructor(seed: number) {
    this.s = seed >>> 0 || 1;
  }
  /** Uniform in [-1, 1). */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (((t ^ (t >>> 14)) >>> 0) / 4294967296) * 2 - 1;
  }
  /** Uniform in [lo, hi). */
  range(lo: number, hi: number): number {
    return lo + ((this.next() + 1) / 2) * (hi - lo);
  }
}

export interface Buffer {
  readonly sampleRate: number;
  readonly data: Float32Array;
}

export function createBuffer(sampleRate: number, seconds: number): Buffer {
  return { sampleRate, data: new Float32Array(Math.max(1, Math.ceil(sampleRate * seconds))) };
}

/** Add `src` into `dst` starting at `offsetSeconds`, scaled by `gain`. */
export function mixInto(dst: Buffer, src: Buffer, offsetSeconds: number, gain = 1): void {
  const start = Math.floor(offsetSeconds * dst.sampleRate);
  const n = Math.min(src.data.length, dst.data.length - start);
  for (let i = 0; i < n; i++) {
    if (start + i < 0) continue;
    dst.data[start + i] = (dst.data[start + i] ?? 0) + (src.data[i] ?? 0) * gain;
  }
}

/**
 * One decaying modal resonance — a struck object's ringing partial.
 *
 * `q` maps to decay length: a high-Q metal die rings, a low-Q wooden one knocks.
 */
export function modalRing(
  sampleRate: number,
  freq: number,
  q: number,
  decay: number,
  amplitude: number,
  seconds: number,
  phase = 0,
): Buffer {
  const buf = createBuffer(sampleRate, seconds);
  // Higher Q lengthens the effective decay; the /12 divisor was tuned so that
  // metal (q=46) rings for roughly its stated 0.85 s and wood (q=9) does not.
  const tau = decay * (1 + q / 12);
  const omega = (2 * Math.PI * freq) / sampleRate;
  for (let i = 0; i < buf.data.length; i++) {
    const env = Math.exp(-i / (tau * sampleRate));
    if (env < 1e-5) break;
    buf.data[i] = Math.sin(omega * i + phase) * env * amplitude;
  }
  return buf;
}

/** Exponentially decaying noise burst — the transient "click" of contact. */
export function noiseBurst(
  sampleRate: number,
  rng: NoiseRng,
  attack: number,
  decay: number,
  amplitude: number,
): Buffer {
  const seconds = attack + decay * 4;
  const buf = createBuffer(sampleRate, seconds);
  const attackSamples = Math.max(1, Math.floor(attack * sampleRate));
  for (let i = 0; i < buf.data.length; i++) {
    const env =
      i < attackSamples
        ? i / attackSamples
        : Math.exp(-(i - attackSamples) / (decay * sampleRate));
    if (i > attackSamples && env < 1e-5) break;
    buf.data[i] = rng.next() * env * amplitude;
  }
  return buf;
}

/** One-pole low-pass. `cutoff` in Hz. Mutates and returns `buf`. */
export function lowpass(buf: Buffer, cutoff: number): Buffer {
  const dt = 1 / buf.sampleRate;
  const rc = 1 / (2 * Math.PI * Math.max(1, cutoff));
  const alpha = dt / (rc + dt);
  let prev = 0;
  for (let i = 0; i < buf.data.length; i++) {
    prev += alpha * ((buf.data[i] ?? 0) - prev);
    buf.data[i] = prev;
  }
  return buf;
}

/** One-pole high-pass. Mutates and returns `buf`. */
export function highpass(buf: Buffer, cutoff: number): Buffer {
  const dt = 1 / buf.sampleRate;
  const rc = 1 / (2 * Math.PI * Math.max(1, cutoff));
  const alpha = rc / (rc + dt);
  let prevIn = 0;
  let prevOut = 0;
  for (let i = 0; i < buf.data.length; i++) {
    const x = buf.data[i] ?? 0;
    prevOut = alpha * (prevOut + x - prevIn);
    prevIn = x;
    buf.data[i] = prevOut;
  }
  return buf;
}

/** Band-pass built from the two one-poles above. Cheap and adequate here. */
export function bandpass(buf: Buffer, low: number, high: number): Buffer {
  return lowpass(highpass(buf, low), high);
}

/**
 * Noise swept between two cutoffs — the sound of paper sliding against paper.
 * Card friction is this and nothing else.
 */
export function noiseSweep(
  sampleRate: number,
  rng: NoiseRng,
  seconds: number,
  fromHz: number,
  toHz: number,
  amplitude: number,
): Buffer {
  const buf = createBuffer(sampleRate, seconds);
  const n = buf.data.length;
  let prev = 0;
  for (let i = 0; i < n; i++) {
    const t = i / n;
    // Shaped so the friction swells then dies, like a hand moving a card.
    const env = Math.sin(Math.PI * t) ** 1.5;
    const cutoff = fromHz + (toHz - fromHz) * t;
    const dt = 1 / sampleRate;
    const rc = 1 / (2 * Math.PI * Math.max(1, cutoff));
    const alpha = dt / (rc + dt);
    prev += alpha * (rng.next() - prev);
    buf.data[i] = prev * env * amplitude;
  }
  return buf;
}

/** Struck tone with a timbre-specific partial stack. Used for reward signatures. */
export function struckTone(
  sampleRate: number,
  freq: number,
  seconds: number,
  timbre: 'marimba' | 'glockenspiel' | 'bell',
  amplitude: number,
): Buffer {
  const buf = createBuffer(sampleRate, seconds);
  const partials: readonly [number, number][] =
    timbre === 'marimba'
      ? [[1, 1.0], [3.9, 0.2], [9.2, 0.05]]
      : timbre === 'glockenspiel'
        ? [[1, 1.0], [2.76, 0.45], [5.4, 0.2], [8.9, 0.08]]
        : [[1, 1.0], [2.0, 0.5], [2.97, 0.35], [4.1, 0.22], [5.43, 0.12]];
  const decay = timbre === 'marimba' ? 0.18 : timbre === 'glockenspiel' ? 0.5 : 1.1;

  for (const [ratio, gain] of partials) {
    const f = freq * ratio;
    if (f > sampleRate / 2) continue; // never alias
    const omega = (2 * Math.PI * f) / sampleRate;
    // Higher partials die faster, which is what makes a struck tone read as struck.
    const tau = decay / (1 + (ratio - 1) * 0.35);
    for (let i = 0; i < buf.data.length; i++) {
      const env = Math.exp(-i / (tau * sampleRate));
      if (env < 1e-5) break;
      buf.data[i] = (buf.data[i] ?? 0) + Math.sin(omega * i) * env * gain * amplitude;
    }
  }
  return buf;
}

/** Detuned pair, for magic shimmer and unsettling Fate Debt textures. */
export function shimmer(
  sampleRate: number,
  freq: number,
  seconds: number,
  detuneCents: number,
  amplitude: number,
): Buffer {
  const buf = createBuffer(sampleRate, seconds);
  const f2 = freq * Math.pow(2, detuneCents / 1200);
  const w1 = (2 * Math.PI * freq) / sampleRate;
  const w2 = (2 * Math.PI * f2) / sampleRate;
  for (let i = 0; i < buf.data.length; i++) {
    const t = i / buf.data.length;
    const env = Math.sin(Math.PI * t) ** 2;
    buf.data[i] = (Math.sin(w1 * i) + Math.sin(w2 * i)) * 0.5 * env * amplitude;
  }
  return buf;
}

/** Descending sine sweep — the sub drop under a mythic reward. */
export function subDrop(
  sampleRate: number,
  fromHz: number,
  toHz: number,
  seconds: number,
  amplitude: number,
): Buffer {
  const buf = createBuffer(sampleRate, seconds);
  const n = buf.data.length;
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const t = i / n;
    const f = fromHz * Math.pow(toHz / fromHz, t);
    phase += (2 * Math.PI * f) / sampleRate;
    buf.data[i] = Math.sin(phase) * Math.exp(-t * 2.5) * amplitude;
  }
  return buf;
}

/** Reverse a buffer in place — the "something is about to happen" pre-swell. */
export function reverse(buf: Buffer): Buffer {
  buf.data.reverse();
  return buf;
}

/** Fade in/out to prevent clicks at boundaries. */
export function applyFades(buf: Buffer, fadeIn = 0.002, fadeOut = 0.01): Buffer {
  const inN = Math.min(buf.data.length, Math.floor(fadeIn * buf.sampleRate));
  const outN = Math.min(buf.data.length, Math.floor(fadeOut * buf.sampleRate));
  for (let i = 0; i < inN; i++) buf.data[i] = (buf.data[i] ?? 0) * (i / inN);
  for (let i = 0; i < outN; i++) {
    const idx = buf.data.length - 1 - i;
    buf.data[idx] = (buf.data[idx] ?? 0) * (i / outN);
  }
  return buf;
}

/** Peak-normalise to `target`. A silent buffer is returned unchanged. */
export function normalize(buf: Buffer, target = 0.89): Buffer {
  let peak = 0;
  for (const v of buf.data) peak = Math.max(peak, Math.abs(v));
  if (peak < 1e-8) return buf;
  const scale = target / peak;
  for (let i = 0; i < buf.data.length; i++) buf.data[i] = (buf.data[i] ?? 0) * scale;
  return buf;
}

/** Soft clip, so a dense mix saturates musically instead of tearing. */
export function softClip(buf: Buffer, drive = 1): Buffer {
  for (let i = 0; i < buf.data.length; i++) {
    buf.data[i] = Math.tanh((buf.data[i] ?? 0) * drive);
  }
  return buf;
}

/**
 * Cross-fade the tail of a buffer over its head so it loops seamlessly.
 * Returns a shortened buffer of `length - crossfade` samples.
 */
export function makeSeamlessLoop(buf: Buffer, crossfadeSeconds: number): Buffer {
  const xf = Math.min(Math.floor(crossfadeSeconds * buf.sampleRate), Math.floor(buf.data.length / 3));
  if (xf <= 0) return buf;
  const outLen = buf.data.length - xf;
  const out = createBuffer(buf.sampleRate, outLen / buf.sampleRate);
  out.data.set(buf.data.subarray(0, outLen));
  for (let i = 0; i < xf; i++) {
    const w = i / xf;
    const tail = buf.data[outLen + i] ?? 0;
    out.data[i] = (out.data[i] ?? 0) * w + tail * (1 - w);
  }
  return out;
}

/** Peak amplitude, for tests and loudness checks. */
export function peakOf(buf: Buffer): number {
  let peak = 0;
  for (const v of buf.data) peak = Math.max(peak, Math.abs(v));
  return peak;
}

/** RMS amplitude. */
export function rmsOf(buf: Buffer): number {
  let sum = 0;
  for (const v of buf.data) sum += v * v;
  return Math.sqrt(sum / Math.max(1, buf.data.length));
}

export function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}
